import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCategoryNodeQuery } from 'lib-knowledge-management-common/graphql';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

import useCategoryDelete from 'hooks/useCategoryDelete';

type CategoryDeleteModalProps = {
  categoryId: string;
};

export function CategoryDeleteModal({
  categoryId,
}: CategoryDeleteModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const { data } = useCategoryNodeQuery({
    variables: {
      id: categoryId,
    },
    nextFetchPolicy: 'cache-only',
  });
  const category = data?.category;
  const [deleteFn, { loading }] = useCategoryDelete();

  const categoryTitle =
    category && extractTitleFromTranslations(category.titles);

  const deleteCategory = useCallback(() => {
    async function handleDeleteArticle() {
      if (!category) return;

      try {
        const res = await deleteFn({ category });
        if (!res.data) {
          throw new Error('No data returned from deleteArticle');
        }
        hideModal();
        successNotify({
          title: 'Article Deleted',
          text: `"${categoryTitle}" has been deleted`,
        });
      } catch (_error) {
        errorNotify({
          text: `"${categoryTitle}" could not be deleted`,
        });
      }
    }
    handleDeleteArticle();
  }, [
    category,
    hideModal,
    categoryTitle,
    deleteFn,
    errorNotify,
    successNotify,
  ]);

  return (
    <ModalWrapper>
      <ModalHead title="Delete Article" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you wish to delete{' '}
          <strong>&quot;{categoryTitle}&quot;</strong>?
        </Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button disabled={loading} onClick={deleteCategory}>
            Delete
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
